import { useCallback, useEffect, useState } from "react";

/**
 * The current path, and a way to change it without a page load.
 *
 * Hand-rolled rather than `react-router`, for the same reason this repo hand
 * rolls its SVG and its data hooks: six routes with no nesting, no loaders and
 * no parameters need about forty lines, and a router would be the largest
 * dependency in the browser bundle to answer a question this small.
 *
 * `popstate` covers the back and forward buttons. It does *not* fire for our
 * own `pushState` calls, which is why `navigate` sets the state itself rather
 * than waiting to be told.
 */
export interface Route {
  path: string;
  /** Pushes a new entry, so back returns to where the reader was. */
  navigate: (path: string) => void;
  /** Rewrites the current entry, for correcting a URL nobody chose. */
  replace: (path: string) => void;
}

function currentPath(): string {
  // `window` is guarded because the unit suite renders in jsdom without a
  // location on a couple of paths, and a throw here would take the whole app
  // down rather than degrade to the landing view.
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

export function useRoute(): Route {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const onPop = () => setPath(currentPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next: string) => {
    // Same path, same state: pushing would stack duplicate history entries, so
    // the back button would appear to do nothing several times over.
    if (next === currentPath()) return;
    window.history.pushState(null, "", next);
    setPath(next);
    // A new page starts at the top. Browsers restore scroll for a real
    // navigation; a pushState is not one, so this is ours to do.
    window.scrollTo(0, 0);
  }, []);

  const replace = useCallback((next: string) => {
    if (next === currentPath()) return;
    window.history.replaceState(null, "", next);
    setPath(next);
  }, []);

  return { path, navigate, replace };
}

/**
 * Whether a click on a link should be handled in the page or left to the
 * browser.
 *
 * A modified click is the reader deliberately asking for something this app
 * cannot do -- a new tab, a new window, a download, a saved link -- and
 * calling `preventDefault` on it is how an SPA breaks middle-click. That is
 * most of the reason the navigation is anchors rather than buttons, so
 * honouring it is the point rather than a nicety.
 */
export function isPlainLeftClick(event: React.MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}
